import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { Subscription } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { PatientsService } from '../../core/services/patients.service';
import { DeliveryDto, PatientDto, Status, Shop } from '@bemum/api-interfaces';

enum State {
  LOADING,
  SUCCESS,
  ERROR,
}

@Component({
  selector: 'bemum-deliveries',
  templateUrl: './deliveries.component.html',
  styleUrls: ['./deliveries.component.scss'],
  standalone: false,
})
export class DeliveriesComponent implements OnInit, OnDestroy {
  @Input() patient: PatientDto;

  private subscriptions: Subscription[] = [];
  public deliveries: DeliveryDto[] = [];
  public currentState = State.LOADING;
  public State = State;
  public Status = Status;
  public Shop = Shop;

  constructor(
    private readonly patientsService: PatientsService,
    private readonly notificationService: NzNotificationService,
  ) {}

  ngOnInit(): void {
    if (!this.patient?.id) {
      this.currentState = State.ERROR;
      return;
    }

    const deliveryRequest = this.patientsService
      .getPatientDeliveries(this.patient.id)
      .pipe(
        finalize(() => (this.currentState = this.deliveries.length > 0 ? State.SUCCESS : State.ERROR)),
        catchError((error) => {
          this.notificationService.error('Erreur', 'Impossible de récupérer l’historique des livraisons.');
          this.currentState = State.ERROR;
          throw error;
        }),
      )
      .subscribe((deliveries) => {
        this.deliveries = deliveries;
      });

    this.subscriptions.push(deliveryRequest);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subs) => subs.unsubscribe());
  }

  getDeliveryStatusTag(status: Status): { text: string; color: string } {
    return (
      {
        [Status.UPCOMING]: { text: 'À venir', color: 'blue' },
        [Status.TO_SEND]: { text: 'À expédier', color: 'orange' },
        [Status.SENT]: { text: 'Expédié', color: 'green' },
        [Status.CANCELED]: { text: 'Annulé', color: 'red' },
        [Status.OVERDUE]: { text: 'En retard', color: 'magenta' },
      }[status] || { text: 'Inconnu', color: 'default' }
    );
  }
  getDeliveryTypeTag(deliveryType: string): { text: string; color: string } {
    return deliveryType === 'SHOP' ? { text: 'Achat unique', color: 'green' } : { text: 'Abonnement', color: 'blue' };
  }

  getProductLabel(productType: string): string {
    const productLabels: { [key: string]: string } = {
      ESSENTIAL_SUPPLEMENTS: 'Compléments Essentiels',
      ESSENTIAL_SUPPLEMENTS_3_MONTHS: 'Compléments Essentiels (3 mois)',
      ESSENTIAL_SUPPLEMENTS_6_MONTHS: 'Compléments Essentiels (6 mois)',
      PROTECTEUR: 'Spécificité +35 ans',
      PROTECTEUR_3_MONTHS: 'Spécificité +35 ans (3 mois)',
      PROTECTEUR_6_MONTHS: 'Spécificité +35 ans (6 mois)',
      STRUCTURANT: 'Spécificité Poids / SOPK',
      STRUCTURANT_3_MONTHS: 'Spécificité Poids / SOPK (3 mois)',
      STRUCTURANT_6_MONTHS: 'Spécificité Poids / SOPK (6 mois)',
      SHOP_ESSENTIAL_SUPPLEMENT: 'E+ / Fertilité',
      SHOP_OMEGA_SUPPLEMENT: 'Oméga',
      SHOP_PROTECTEUR: 'P+ / Protecteur',
      SHOP_STRUCTURANT: 'S+ / Structurant',
      SHOP_COACHING: 'Coaching',
    };

    return productLabels[productType] || 'Produit inconnu';
  }
}
